/**
 * Utilities to decode HTML entities (e.g. &#39;, &amp;) in record data that is
 * rendered as PLAIN TEXT (e.g. Form.io read-only fields / input [value]), so the
 * proper characters are shown instead of the raw encoded form.
 *
 * IMPORTANT: Only use this on data that is rendered as plain text. Do NOT use it
 * on data that is later rendered via [innerHTML] (e.g. the smart-table, which uses
 * the safeHtml pipe) - the browser already decodes entities when parsing innerHTML,
 * so decoding here as well would double-decode and could break markup / be unsafe.
 */

let decoderEl: HTMLTextAreaElement | null = null;

/**
 * Decode HTML entities in a single string. Loops so that multiple levels of
 * encoding (e.g. `&amp;#39;` -> `&#39;` -> `'`) are fully decoded.
 */
export function decodeHtmlEntities(value: string): string {
  // nothing to decode if there is no entity marker
  if (!value || value.indexOf('&') === -1) {
    return value;
  }
  if (!decoderEl) {
    decoderEl = document.createElement('textarea');
  }
  let current = value;
  decoderEl.innerHTML = current;
  let decoded = decoderEl.value;
  while (decoded !== current) {
    current = decoded;
    decoderEl.innerHTML = current;
    decoded = decoderEl.value;
  }
  return decoded;
}

/**
 * Recursively decode HTML entities in every string value of an object/array.
 * Mutates and returns the given value (non-string/object values are returned
 * unchanged).
 */
export function decodeHtmlEntitiesDeep(value: any): any {
  if (typeof value === 'string') {
    return decodeHtmlEntities(value);
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = decodeHtmlEntitiesDeep(value[i]);
    }
    return value;
  }
  if (value && typeof value === 'object') {
    for (const key of Object.keys(value)) {
      value[key] = decodeHtmlEntitiesDeep(value[key]);
    }
    return value;
  }
  return value;
}
